import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, Button, Space, message } from 'antd';
import {
    LoadingOutlined,
    DownloadOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    FilePdfOutlined,
    ExpandOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSharedData } from '../contexts/SharedDataContext';
import styles from './CustomReportModal.module.css';

// Custom spinner icon for loading state
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const CustomReportModal = ({
                               visible,
                               reportName,
                               params = {},
                               onClose,

                               // NEW PROPS for direct data mode
                               mode = 'api', // 'api' (existing) or 'direct' (new)
                               directReportData = null, // Report data provided directly (from AI agent)
                               title = null // Override title when using direct mode
                           }) => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getAccessTokenSilently } = useAuth0();
    const { selectedTenant } = useSharedData();
    const iframeRef = useRef(null);

    // Effect for API mode (existing functionality)
    useEffect(() => {
        if (visible && mode === 'api' && reportName) {
            let isMounted = true;

            const fetchReportData = async () => {
                setLoading(true);
                try {
                    const token = await getAccessTokenSilently();

                    // Build query string from params
                    const queryParams = new URLSearchParams({
                        ...params,
                        format: 'html,json,csv' // Request all formats
                    }).toString();

                    const response = await fetch(`/api/reports/custom/${reportName}?${queryParams}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'x-tenant': selectedTenant,
                        },
                    });

                    if (!response.ok) {
                        throw new Error('Failed to fetch report data');
                    }

                    const data = await response.json();

                    // Only update state if the component is still mounted
                    if (isMounted) {
                        setReportData(data);
                        setLoading(false);
                    }
                } catch (err) {
                    console.error('Error fetching report data:', err);
                    // Only update state if the component is still mounted
                    if (isMounted) {
                        setError('Failed to fetch report: ' + err.message);
                        setLoading(false);
                    }
                }
            };

            fetchReportData();

            // Cleanup function to prevent state updates after unmount
            return () => {
                isMounted = false;
            };
        }
    }, [visible, mode, reportName, params, getAccessTokenSilently, selectedTenant]);

    // Effect for direct mode (new functionality)
    useEffect(() => {
        if (visible && mode === 'direct' && directReportData) {
            // Use the provided data directly
            setReportData(directReportData);
            setLoading(false);
            setError(null);
        }
    }, [visible, mode, directReportData]);

    // Reset state when modal becomes invisible
    useEffect(() => {
        if (!visible) {
            setReportData(null);
            setLoading(false);
            setError(null);
        }
    }, [visible]);

    // Handle downloading the report in different formats
    const handleDownload = (format) => {
        if (!reportData) return;

        try {
            let content, filename, type;

            // Determine the base filename
            const baseFilename = mode === 'direct' ?
                (title || directReportData?.title || 'ai-report') :
                reportName;

            if (format === 'csv' && reportData.csv) {
                content = reportData.csv;
                filename = `${baseFilename}.csv`;
                type = 'text/csv';
            } else if (format === 'json' && reportData.json) {
                content = JSON.stringify(reportData.json, null, 2);
                filename = `${baseFilename}.json`;
                type = 'application/json';
            } else {
                message.error(`${format.toUpperCase()} data not available`);
                return;
            }

            // Create download link
            const blob = new Blob([content], { type });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success(`Downloaded ${filename}`);
        } catch (err) {
            console.error('Error downloading report:', err);
            message.error('Failed to download report');
        }
    };

    // Handle printing to PDF
    const handlePrintToPdf = () => {
        if (iframeRef.current) {
            try {
                // Get the iframe window
                const iframeWindow = iframeRef.current.contentWindow;

                // Add print-specific styling to the iframe document if needed
                const style = iframeWindow.document.createElement('style');
                style.textContent = `
                    @media print {
                        body {
                            print-color-adjust: exact;
                            -webkit-print-color-adjust: exact;
                        }
                    }
                `;
                iframeWindow.document.head.appendChild(style);

                // Call print on the iframe's window
                iframeWindow.focus();
                iframeWindow.print();

                message.success('Preparing PDF download...');
            } catch (err) {
                console.error('Error generating PDF:', err);
                message.error('Failed to generate PDF');
            }
        } else {
            message.error('Report preview not available');
        }
    };

    // Handle opening the report in a new tab
    const handleOpenInNewTab = () => {
        if (!reportData || !reportData.html) {
            message.error('Report HTML not available');
            return;
        }

        try {
            // Create a blob with the HTML content
            const blob = new Blob([reportData.html], { type: 'text/html' });
            const url = URL.createObjectURL(blob);

            // Open the URL in a new tab
            window.open(url, '_blank');

            // Clean up the URL object after a delay to ensure the browser has time to open it
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);
        } catch (err) {
            console.error('Error opening report in new tab:', err);
            message.error('Failed to open report in new tab');
        }
    };

    // Determine the modal title
    const getModalTitle = () => {
        if (mode === 'direct') {
            return title || directReportData?.title || 'AI Analysis Report';
        }
        return reportName ? `Report: ${reportName}` : 'Custom Report';
    };

    // Show record count in footer for direct mode
    const getRecordCount = () => {
        if (mode === 'direct' && directReportData?.recordCount) {
            return ` (${directReportData.recordCount} records)`;
        }
        if (mode === 'api' && reportData?.json?.length) {
            return ` (${reportData.json.length} records)`;
        }
        return '';
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            width={1400}
            style={{ top: 20 }}
            bodyStyle={{
                padding: 0,
                height: 'calc(100vh - 180px)',
                position: 'relative',
                overflow: 'hidden'
            }}
            destroyOnClose={true}
            maskClosable={true}
            title={getModalTitle()}
            styles={{ header: { marginBottom: 0, padding: '16px 24px 8px' } }}
            footer={
                <Space style={{ paddingTop: '12px' }}>
                    <span style={{ color: '#666', fontSize: '12px', marginRight: 'auto' }}>
                        {mode === 'direct' && directReportData?.description && (
                            <span>{directReportData.description}{getRecordCount()}</span>
                        )}
                        {mode === 'api' && getRecordCount() && (
                            <span>Records{getRecordCount()}</span>
                        )}
                    </span>

                    <Button onClick={onClose}>Close</Button>
                    <Button
                        onClick={() => handleDownload('csv')}
                        icon={<FileExcelOutlined style={{ color: '#10b981' }} />}
                        disabled={!reportData || !reportData.csv}
                    >
                        Download CSV
                    </Button>
                    <Button
                        onClick={() => handleDownload('json')}
                        icon={<FileTextOutlined style={{ color: '#3b82f6' }} />}
                        disabled={!reportData || !reportData.json}
                    >
                        Download JSON
                    </Button>
                    <Button
                        onClick={handleOpenInNewTab}
                        icon={<ExpandOutlined style={{ color: '#6366f1' }} />}
                        disabled={!reportData || !reportData.html}
                    >
                        Open in New Tab
                    </Button>
                    <Button
                        onClick={handlePrintToPdf}
                        icon={<FilePdfOutlined style={{ color: '#ef4444' }} />}
                        disabled={!reportData || !reportData.html}
                    >
                        Print / PDF
                    </Button>
                </Space>
            }
        >
            {loading ? (
                <div className={styles.loadingContainer}>
                    <Spin indicator={antIcon} />
                    <span className={styles.loadingText}>
                        {mode === 'direct' ? 'Preparing report data...' : 'Loading report data...'}
                    </span>
                </div>
            ) : error ? (
                <div className={styles.errorContainer}>
                    {error}
                </div>
            ) : reportData && reportData.html ? (
                <div className={styles.modalContent}>
                    <iframe
                        ref={iframeRef}
                        title="ReportHtml"
                        srcDoc={reportData.html}
                        className={styles.iframe}
                    />
                </div>
            ) : (
                <div className={styles.emptyContainer}>
                    No report data available
                </div>
            )}
        </Modal>
    );
};

export default React.memo(CustomReportModal);
